import {
    CustomPagePathDescriptionVisibility,
    IBindingRule,
    IDynamicPanelItem,
    IHorizontalPanelItem,
    isVirtualPanel,
} from '@se/cp.common';
import { DataBinding } from '@se/se.ui.html5';
import { isEmpty } from 'lodash-es';

import { PropertyType } from './cp-configuration/view-model';

export interface CpBaseNode {
    id: string;
    context?: string;
    hasControls: boolean;
    header: string;
    isVisible: boolean;
    isInError: boolean;
    isModified: boolean;
    isAutoUpdated: boolean;
    hasAIData: boolean;
    // isInWarning: boolean;
    // hasMeasureOutOfRange: boolean;
    warnings: string[];
    level: number;
    parentId?: string;
    pathDescription: string;
    pathDescriptionVisibility: CustomPagePathDescriptionVisibility;
    tabSelectorIds: string[];
}

export interface CpFlatNode extends CpBaseNode {
    children: string[];
    constraints: IBindingRule[];
    createdBy?: string;
    detailIds: string[];
    horizontalIds: string[];
    isVirtual: boolean;
}

export interface CpHorizontalNode extends CpBaseNode {
    columnSpan: number;
}

export type CpNode = CpFlatNode | CpHorizontalNode;

export interface CpNodeFilterStatuses {
    edit: boolean;
    error: boolean;
    warning: boolean;
}

export interface CpNodeFilter {
    readonly status: CpNodeFilterStatuses;
    readonly text: string;
}

export function isCpFlatNode(node: CpNode): node is CpFlatNode {
    return 'children' in node;
}

export function isCpHorizontalNode(node: CpNode): node is CpHorizontalNode {
    return 'columnSpan' in node;
}

export function isCpFlatNodeSelectable(node: CpFlatNode) {
    return !isEmpty(node.children) || node.hasControls || node.detailIds.length > 0 || node.horizontalIds.length > 0;
}

export interface CpTreeNode extends CpBaseNode {
    children: CpTreeNode[];
    detailIds: string[];
    horizontalIds: string[];
    expandable: boolean;
}

export function isCpTreeNodeSelectable(node: CpFlatNode | CpTreeNode) {
    return !isEmpty(node.children) || node.hasControls || node.detailIds.length > 0 || node.horizontalIds.length > 0;
}

export interface CpAddMenuItem {
    dataBinding: DataBinding;
    description?: string;
    value?: PropertyType;
}

export function cpNodesFlatToTree(allNodes: CpFlatNode[], parentId?: string, level = 0): CpTreeNode[] {
    const nodes = allNodes.filter((node) => {
        if (!parentId) {
            return !node.parentId;
        }

        return parentId === node.parentId;
    });

    return nodes.map((node) => {
        const children = cpNodesFlatToTree(allNodes, node.id, level + 1);

        return {
            ...node,
            children,
            expandable: true,
            level,
        };
    });
}

export function cpNodeFromPanel(panel: IDynamicPanelItem): CpFlatNode {
    return {
        // Rimuovi i figli virtuali.
        children: panel.dynamicPanelItems?.filter((p) => !isVirtualPanel(p)).map((p) => p.id) ?? [],
        createdBy: panel.createdBy,
        constraints: panel.constraints || [],
        context: undefined,
        detailIds: panel.detailPanelItems?.map((p) => p.id) ?? [],
        hasControls: !isEmpty(panel.controlPanel?.controls),
        header: panel.header,
        horizontalIds: panel.horizontalPanelItems?.map((p) => p.id) ?? [],
        id: panel.id,
        isInError: false,
        warnings: [],
        isModified: false,
        isAutoUpdated: false,
        hasAIData: false,
        isVirtual: isVirtualPanel(panel),
        isVisible: true,
        level: 0,
        parentId: undefined,
        pathDescription: panel.header,
        pathDescriptionVisibility: panel.pathDescriptionVisibility,
        tabSelectorIds: [],
    };
}

export function cpNodeMatchesFilters(filter: CpNodeFilter, node?: CpFlatNode) {
    if (!node) {
        return false;
    }

    // Se non ci sono filtri, il nodo è visibile
    if (!filter.status.edit && !filter.status.error && !filter.status.warning && !filter.text) {
        return true;
    }

    let matches = true;

    if (filter.status.edit) {
        matches = matches && node.isModified;
    }
    if (filter.status.error) {
        matches = matches && node.isInError;
    }
    if (filter.status.warning) {
        matches = matches && Boolean(node.warnings.length);
    }
    if (filter.text) {
        matches = matches && new RegExp(filter.text, 'i').test(node.header);
    }

    return matches;
}

export function cpHorizontalNodeFromPanel(panel: IHorizontalPanelItem, level: number): CpHorizontalNode {
    return {
        columnSpan: panel.columnSpan,
        context: undefined,
        hasControls: !isEmpty(panel.controlPanel?.controls),
        header: panel.header,
        id: panel.id,
        isInError: false,
        isModified: false,
        isAutoUpdated: false,
        hasAIData: false,
        warnings: [],
        isVisible: true,
        level: level + 1,
        parentId: undefined,
        pathDescription: panel.header,
        pathDescriptionVisibility: panel.pathDescriptionVisibility,
        tabSelectorIds: [],
    };
}
